import { useTranslations } from '@vocab/react';
import { Columns, Column, Stack, Text } from 'braid-design-system';
import type { RefObject } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { getProductFeatureByType } from 'src/app/utils/getProductFeatureByType';
import { BrandingContextualHelp } from 'src/app/v1/views/components/BrandingContextualHelp';
import { KeySellingPoints } from 'src/app/v1/views/components/KeySellingPoints';
import { BrandingEditView } from 'src/app/v1/views/pages/Edit/components/BrandingEditView';
import type { AppState } from 'src/types/appState';
import type { KeySellingPointsRef } from 'src/types/formFields';

import translations from './.vocab';

interface EditProps {
  refs: {
    keySellingPointsRef: RefObject<KeySellingPointsRef>;
  };
}

export const Edit = ({ refs }: EditProps) => {
  const { isPrivateAdvertiser } = useSelector(
    (state: AppState) => ({
      isPrivateAdvertiser: state.session.hirerAccount?.isPrivateAdvertiser,
    }),
    shallowEqual,
  );
  const { t } = useTranslations(translations);

  const { cartFeatures } = useShoppingCart();

  const brandingFeature = getProductFeatureByType({
    featureType: 'Branding',
    productFeatures: cartFeatures,
  });

  const keySellingPointsFeature = getProductFeatureByType({
    featureType: 'BulletPoints',
    productFeatures: cartFeatures,
  });

  return (
    <Stack space="medium">
      <Stack space="small">
        {Boolean(brandingFeature) ? (
          <>
            <Columns space="none">
              <Column>
                <Text weight="strong">
                  {t('upsell-section-read-edit-branding-label')}
                </Text>
              </Column>
              {isPrivateAdvertiser ? null : (
                <Column width="content">
                  <BrandingContextualHelp />
                </Column>
              )}
            </Columns>
            <BrandingEditView customFeatures={cartFeatures} />{' '}
          </>
        ) : null}
      </Stack>
      {Boolean(keySellingPointsFeature) ? (
        <KeySellingPoints ref={refs.keySellingPointsRef} />
      ) : null}
    </Stack>
  );
};
